import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, ChevronRight, ChevronDown, Zap, Video, Leaf, Droplet, Sparkles, Calendar, PlayCircle, UtensilsCrossed, Lightbulb } from 'lucide-react';
import InquiryFormModal from '../components/InquiryFormModal';
import InfoSessionButton from '../components/InfoSessionButton';
import ShoppingListDrawer from '../components/ShoppingListDrawer';
import DoubleDownMealMap from '../components/DoubleDownMealMap';
import FoodsOverviewChart from '../components/FoodsOverviewChart';
import { useAuth } from '../context/AuthContext';

const BOOKING_URL = 'https://www.wellnessliving.com/rs/event/breathing_room-rws2se?k_class=985717&k_class_tab=75840';
const ZOOM_URL = 'https://us06web.zoom.us/j/86842248085?pwd=GN1PZcEDzx33bHqBHaX5a5kntZkKGC.1';

const includes = [
  { icon: Video, title: '2 Fascial Maneuver practices daily', text: 'Zoom-led sessions (6:30 AM & 7:30 PM) or a follow-along video option — your choice, every day.' },
  { icon: Leaf, title: 'Cleansing recipes', text: 'Real, nourishing recipes designed to support your body as it resets.' },
  { icon: Check, title: 'Meal plans', text: 'Structured, easy-to-follow plans so you always know what comes next.' },
  { icon: Droplet, title: 'Vitality-boosting elixirs', text: 'Simple, potent elixirs to help boost your energy and vitality.' },
];

// ============ INTRO VIDEOS (embedId to be added later) ============
const introVideos = [
  { key: 'welcome', title: 'Welcome to The Double Down', subtitle: 'Your intro & orientation', embedId: '4FdAq1BvW7o' },
  { key: 'stress-intro', title: 'Intro to Fascial Maneuvers & the Stress Reset', subtitle: 'How the daily practices work', embedId: 'IB10DIqV_b0' },
  { key: 'prep', title: 'Web Program Overview', subtitle: 'How to find your way around', embedId: 'wx3bq83TCMw' },
];

// ============ 7 DAYS × 2 FASCIA PRACTICES ============
// Per-day video overrides (from the 28-Day Stress Reset library). Keyed by slot: 0 = Practice 1, 1 = Practice 2.
const DD_DAY_VIDEO_OVERRIDES = {
  1: { 0: { title: 'Stress Reset', embedId: 'pEOet1cf_z8' }, 1: { title: 'Organs & Stored Emotions', embedId: 'hv--9WSB0l4' } },
  2: { 0: { title: 'Palate Swipe, Totally Twisted, Psoas & Jaw Release', embedId: 'jzOa6eOQKDM' }, 1: { title: 'Restorative Organ Reset', embedId: 'yimMFzsv2_I' } },
  3: { 0: { title: 'Stress Reset — Chair Practice', embedId: 'qfDJOckl2jk' }, 1: { title: 'Organ Reset', embedId: 'EcEkqCtqSx8' } },
  4: { 0: { title: 'Vagus Nerve Reset, Totally Twisted & Anti-Gravity (16 min)', embedId: '2H5G_xddmTQ' }, 1: { title: 'Organ Reset with Affirmations', embedId: '4HlaO_q08Ao' } },
  5: { 0: { title: 'Totally Twisted, Swinger, Pull Over & Sinus Reset', embedId: 'XMdErCVOOUs' }, 1: { title: 'Vagus Nerve Reset — Ankles, Knees, Hamstrings, Psoas & Jaw', embedId: 'hs9RROigS0Q' } },
  6: { 0: { title: 'Stress Release', embedId: 'TK7mUqKEI4A' }, 1: { title: 'All Healing Is Self Healing', embedId: 'T4_BPLooxpI' } },
  7: { 0: { title: 'Stress Reset — Totally Twisted, Anti-Gravity, Elbow Torque & Forehead Release', embedId: 'c-8RuEgU3Fw' }, 1: { title: 'Fascial Coffee — Upper Body Reset', embedId: 'b38UD_SyY3U', note: 'Instead of an organ reset today, we\u2019ll try a slightly different practice. You may find you love this one!' } },
};
const dayPractices = Array.from({ length: 7 }, (_, i) => {
  const day = i + 1;
  const ov = DD_DAY_VIDEO_OVERRIDES[day] || {};
  return {
    day,
    practices: [0, 1].map((slot) => ov[slot] || { title: `Day ${day} · Practice ${slot + 1}`, embedId: null }),
  };
});

// ============ MY RECOMMENDATIONS ============
const ddRecommendations = [
  {
    title: 'Make the Lemon Elixir ahead — ready for Day 1 (Tuesday)',
    body: "Have the Lemon Elixir made and waiting in the fridge so it's ready for easy meals or snacks whenever you need it. Drink roughly half a cup each morning. It will keep easily for the first three days — up to five days in the fridge — so place it wherever it feels appropriate in your week. It's really great for taking the edge off hunger before a meal.",
  },
  {
    title: 'Celery juice on the weekends',
    body: "On the weekends, when you have a little more time, try fresh celery juice — it's best made fresh and consumed within one to two hours. You can make it in a high-powered blender or a juicer. Drink it immediately, roughly one cup (about eight stalks, or roughly one head of celery). It doesn't have to be plain celery either — you can mix in a little carrot and apple, or anything you like, but try to keep the bulk of it celery. Having it both weekend days helps your body do a little \u201cclear out\u201d — it primes the pump and supports your organs.",
  },
  {
    title: 'Make a larger salad to carry you through several days',
    body: "Consider making one of the larger salads — the Spicy Chicken Salad or the Detox Salad is perfect. Made according to the instructions, it'll keep roughly four to five days in the fridge. Enjoy it as a salad and change up the protein to whatever works for you that day, sauté it into a warm meal (paired with a protein), or turn it into a soup. One prep, lots of flexible meals.",
  },
  {
    title: 'Make your own stock or broth',
    body: "Consider making some of your own stock or broth. If you have a crockpot, it's perfect — throw in a bunch of vegetable ends. You can also make bone broth using a beef, turkey, or chicken bone, whatever works best for you. It pairs beautifully with a quick soup or a Magic Chili, which is wonderful for breakfast, lunch, or dinner. And if you'd like something to take the edge off hunger, a quick snack, or an evening craving, consider making the Magic Bars or the gluten-free Banana Bread.",
  },
  {
    title: 'Remember: this is anti-inflammatory, not calorie-cutting',
    body: "It's important to remember that this is not a calorie-cutting program — it's an anti-inflammatory one. For seven days we're eating anti-inflammatory foods and doing the fascia maneuvers to augment the body's anti-inflammatory and lymph-drainage capacity. So eat when you're hungry. Try not to cut calories or skip meals, and make sure protein, fat, and fiber are included in your meals. Avocado, seeds, and nuts can really take the edge off hunger.",
  },
  {
    title: 'Pick a tea that works for you',
    body: "There are lots of options, but truly any tea will do. Things like nettle, milk thistle, or a detox tea can help support this process. Have it hot or cold — sometimes I make it ahead and keep cold tea in the fridge, so there's something ready for the moments when you want something and it's already made.",
  },
  {
    title: 'Split a smoothie (or your lunch) for later',
    body: "Sometimes it's a great idea to split a smoothie into two — one for breakfast and one for an afternoon snack, so you've got quick access to something you know is healthy. I do the same with my lunch, splitting it so I know I have food ready when I need it.",
  },
];

// Recipe names mentioned in the recommendations → recipe ids (deep-links to the recipe card).
// Longest phrases first so partial names don't match before the full name.
const REC_RECIPE_LINKS = [
  ['Spicy Chicken Salad', 'spicy-chicken-salad'],
  ['Detox Salad', 'fruit-vegetable-salad'],
  ['Lemon Elixir', 'lemon-elixir'],
  ['Banana Bread', 'banana-bread'],
  ['Magic Chili', 'magic-chili'],
  ['Magic Bars', 'magic-bars'],
  ['Celery juice', 'celery-juice'],
  ['celery juice', 'celery-juice'],
];

// Render a string, turning any known recipe name into a link that opens its recipe card.
const linkifyRecipes = (text) => {
  const pattern = new RegExp(`(${REC_RECIPE_LINKS.map(([name]) => name).join('|')})`, 'g');
  const parts = text.split(pattern);
  return parts.map((part, i) => {
    const match = REC_RECIPE_LINKS.find(([name]) => name === part);
    if (match) {
      return (
        <Link
          key={i}
          to={`/resources/recipes?resource=${match[1]}`}
          className="text-terracotta font-medium underline decoration-terracotta/40 underline-offset-2 hover:decoration-terracotta transition-colors"
          data-testid={`dd-recipe-link-${match[1]}`}
        >
          {part}
        </Link>
      );
    }
    return part;
  });
};

const ddRecommendationsClosing = "You will find your own way — you definitely don't have to do it my way. I've been doing this for many years and experienced many pitfalls along the way. Trust yourself; you'll find your rhythm.";

const VideoSlot = ({ title, subtitle, embedId, testId, note }) => (
  <div className="rounded-xl border border-stone-200 bg-white overflow-hidden" data-testid={testId}>
    {embedId ? (
      <div className="aspect-video">
        <iframe
          className="w-full h-full"
          src={`https://www.youtube.com/embed/${embedId}`}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
        />
      </div>
    ) : (
      <div className="aspect-video bg-stone-50 border-b border-dashed border-stone-200 flex flex-col items-center justify-center text-center px-4">
        <PlayCircle size={40} className="text-stone-300 mb-2" />
        <p className="font-body text-stone-500 text-sm">Video coming soon</p>
        <p className="font-body text-stone-400 text-xs mt-0.5">Jill will add this video here</p>
      </div>
    )}
    <div className="px-4 py-3">
      <p className="font-heading text-base text-forest leading-tight">{title}</p>
      {subtitle && <p className="font-body text-xs text-stone-500 mt-0.5">{subtitle}</p>}
      {note && <p className="font-accent italic text-terracotta text-sm mt-2 leading-relaxed">{note}</p>}
    </div>
  </div>
);

// ============ INTERACTIVE PROGRAM (unlocked users) ============
const DoubleDownProgram = () => {
  const { user } = useAuth();
  const [expandedDay, setExpandedDay] = useState(1);
  const completedKey = user?.id ? `double-down-completed-${user.id}` : null;
  const [completedDays, setCompletedDays] = useState(() => {
    try {
      const v = completedKey ? localStorage.getItem(completedKey) : null;
      return v ? JSON.parse(v) : {};
    } catch {
      return {};
    }
  });
  const toggleComplete = (day) => {
    const next = { ...completedDays, [day]: !completedDays[day] };
    setCompletedDays(next);
    if (completedKey) localStorage.setItem(completedKey, JSON.stringify(next));
  };
  const completedCount = Object.values(completedDays).filter(Boolean).length;
  const progressPct = Math.round((completedCount / 7) * 100);

  return (
    <div className="min-h-screen bg-cream pb-24" data-testid="double-down-program">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-200/50 via-orange-100/40 to-emerald-100/40 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-10">
          <Link
            to="/programs"
            className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-6"
            data-testid="double-down-back-link"
          >
            <ArrowLeft size={18} /> Back to Programs
          </Link>
          <div className="mb-4">
            <span className="inline-flex items-center gap-2 bg-forest text-cream rounded-full px-4 py-1.5">
              <Zap size={15} />
              <span className="text-xs font-body uppercase tracking-[0.15em]">Your Program</span>
            </span>
          </div>
          <h1 className="font-heading text-4xl md:text-6xl text-forest mb-3 leading-tight">The Double Down</h1>
          <p className="font-accent italic text-lg md:text-xl text-[#8a6a5a]">
            A shake up, wake up for your metabolic system — two practices a day, seven days.
          </p>
        </div>
      </section>

      {/* DAILY PRACTICE ZOOM LINK */}
      <section className="max-w-6xl mx-auto px-6 mt-8" data-testid="double-down-zoom">
        <a
          href={ZOOM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gradient-to-r from-violet-500 to-purple-600 text-cream rounded-2xl px-6 py-5 hover:from-violet-600 hover:to-purple-700 transition-colors shadow-md"
          data-testid="double-down-zoom-link"
        >
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-cream/15 flex items-center justify-center flex-shrink-0">
              <Video size={20} />
            </div>
            <div>
              <p className="text-cream/70 font-body text-xs tracking-widest uppercase mb-0.5">Daily Practice · 6:30 AM & 7:30 PM</p>
              <h2 className="font-heading text-xl md:text-2xl leading-tight">Join the live session on Zoom</h2>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 bg-cream text-purple-700 font-body font-medium px-5 py-2.5 rounded-full flex-shrink-0">
            Open Zoom <ChevronRight size={16} />
          </span>
        </a>
      </section>

      {/* INTRO SECTION */}
      <section className="max-w-6xl mx-auto px-6 mt-8" data-testid="double-down-intro">
        <div className="bg-white rounded-3xl shadow-card border border-stone-100 p-6 md:p-8">
          <p className="text-terracotta font-body text-xs tracking-widest uppercase mb-1">Start Here</p>
          <h2 className="font-heading text-2xl md:text-3xl text-forest mb-2">Welcome & Orientation</h2>
          <p className="font-body text-stone-600 text-sm leading-relaxed mb-6 max-w-2xl">
            Watch these short videos first — a warm welcome to the program, a gentle intro to the fascia
            maneuvers you&apos;ll practice each day, and a little more to help you prepare.
          </p>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {introVideos.map((v) => (
              <VideoSlot key={v.key} title={v.title} subtitle={v.subtitle} embedId={v.embedId} testId={`dd-intro-video-${v.key}`} />
            ))}
          </div>
        </div>
      </section>

      {/* DAILY PRACTICES — 7 DAYS × 2 VIDEOS */}
      <section className="max-w-6xl mx-auto px-6 mt-12" data-testid="double-down-days">
        <div className="text-center mb-6">
          <p className="text-xs font-body text-terracotta uppercase tracking-[0.2em] mb-2">Your Daily Practice</p>
          <h2 className="font-heading text-3xl text-forest">7 Days · Two Practices a Day</h2>
        </div>

        {/* PROGRESS TRACKER */}
        <div className="max-w-md mx-auto mb-8" data-testid="dd-progress-tracker">
          <div className="flex items-center justify-between mb-2">
            <span className="font-body text-sm text-forest font-medium">
              {completedCount === 7 ? '🎉 All 7 days complete!' : `${completedCount} of 7 days complete`}
            </span>
            <span className="font-body text-xs text-stone-500">{progressPct}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-stone-200 overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-forest"
              initial={false}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.4 }}
              data-testid="dd-progress-bar"
            />
          </div>
        </div>

        <div className="space-y-3">
          {dayPractices.map(({ day, practices }) => {
            const open = expandedDay === day;
            const done = !!completedDays[day];
            return (
              <div key={day} className={`bg-white rounded-2xl border overflow-hidden transition-colors ${done ? 'border-emerald-300' : 'border-stone-200'}`} data-testid={`dd-day-${day}`}>
                <button
                  onClick={() => setExpandedDay(open ? null : day)}
                  className="w-full px-5 md:px-6 py-4 flex items-center justify-between gap-4 text-left hover:bg-stone-50 transition-colors"
                  data-testid={`dd-day-toggle-${day}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-heading transition-colors ${done ? 'bg-emerald-500 text-white' : 'bg-forest/8 text-forest'}`}>
                      {done ? <Check size={20} /> : day}
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-forest leading-tight flex items-center gap-2">
                        Day {day}
                        {done && <span className="text-emerald-600 text-xs font-body font-medium">✓ Complete</span>}
                      </h3>
                      <p className="font-body text-xs text-stone-500">Two fascial maneuver practices</p>
                    </div>
                  </div>
                  <ChevronDown size={20} className={`text-forest flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <div className="px-5 md:px-6 pb-6 grid gap-5 sm:grid-cols-2">
                        {practices.map((p, idx) => (
                          <VideoSlot key={idx} title={p.title} embedId={p.embedId} note={p.note} testId={`dd-day-${day}-video-${idx + 1}`} />
                        ))}
                      </div>
                      <div className="px-5 md:px-6 pb-6">
                        <button
                          onClick={() => toggleComplete(day)}
                          className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-body font-medium transition-colors ${
                            done
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-300 hover:bg-emerald-100'
                              : 'bg-forest text-cream hover:bg-forest-dark'
                          }`}
                          data-testid={`dd-day-complete-${day}`}
                        >
                          <Check size={18} />
                          {done ? 'Day complete ✓ — tap to undo' : 'Mark Day complete'}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </section>

      {/* MY RECOMMENDATIONS */}
      <section className="max-w-6xl mx-auto px-6 mt-14" data-testid="double-down-recommendations">
        <div className="bg-[#fdf8ee] rounded-3xl shadow-card border border-[#e8dcc3] p-6 md:p-9 relative overflow-hidden">
          {/* soft ruled-paper feel */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.5]"
            style={{
              backgroundImage: 'repeating-linear-gradient(to bottom, transparent, transparent 37px, #d9c8a6 37px, #d9c8a6 38px)',
              maskImage: 'linear-gradient(to bottom, transparent, black 120px)',
              WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 120px)',
            }}
          />
          <div className="relative">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-full bg-terracotta/10 flex items-center justify-center flex-shrink-0">
                <Lightbulb size={20} className="text-terracotta" />
              </div>
              <div>
                <p className="text-terracotta font-body text-xs tracking-widest uppercase">From Jill</p>
                <h2 className="font-hand text-3xl md:text-4xl text-forest leading-none">My Recommendations</h2>
              </div>
            </div>
            <p className="font-hand text-base md:text-lg text-stone-600 leading-snug mb-6 max-w-2xl">
              A few gentle, practical tips to set yourself up for an easeful week.
            </p>
            <div className="space-y-4">
              {ddRecommendations.map((rec, idx) => (
                <div key={idx} className="flex items-start gap-4 rounded-2xl border border-[#e8dcc3] bg-white/60 p-5" data-testid={`dd-recommendation-${idx + 1}`}>
                  <div className="w-9 h-9 rounded-full bg-forest text-cream flex items-center justify-center font-hand text-lg flex-shrink-0 leading-none pt-0.5">
                    {idx + 1}
                  </div>
                  <div>
                    <h3 className="font-hand text-xl md:text-2xl text-forest mb-0.5 leading-tight">{linkifyRecipes(rec.title)}</h3>
                    <p className="font-hand text-base md:text-lg text-stone-700 leading-snug">{linkifyRecipes(rec.body)}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-5 border-t border-[#e8dcc3]" data-testid="dd-recommendations-closing">
              <p className="font-hand text-xl md:text-2xl text-forest leading-snug">
                {ddRecommendationsClosing}
              </p>
              <p className="font-hand text-2xl md:text-3xl text-terracotta mt-3">— Jill</p>
            </div>
          </div>
        </div>
      </section>

      {/* MEAL MAP */}
      <section className="max-w-6xl mx-auto px-6 mt-14" data-testid="double-down-meals">
        <div className="text-center mb-6">
          <p className="text-xs font-body text-terracotta uppercase tracking-[0.2em] mb-2">Nourishment</p>
          <h2 className="font-heading text-3xl text-forest flex items-center justify-center gap-2">
            <UtensilsCrossed size={26} className="text-forest/70" /> Your 7-Day Meal Map
          </h2>
        </div>
        <DoubleDownMealMap />
      </section>

      {/* FOODS THAT NOURISH — OVERVIEW */}
      <section className="max-w-6xl mx-auto px-6 mt-14" data-testid="double-down-foods-overview">
        <div className="text-center mb-6">
          <p className="text-xs font-body text-terracotta uppercase tracking-[0.2em] mb-2">Your Guide</p>
          <h2 className="font-heading text-3xl text-forest">Foods that Nourish</h2>
        </div>
        <FoodsOverviewChart />
      </section>

      {/* Shopping list stays available */}
      <ShoppingListDrawer />
    </div>
  );
};

// ============ SALES / LOCKED VIEW (public) ============
const DoubleDownSales = () => {
  const [inquiry, setInquiry] = useState(null);
  const openInquiry = (meta = {}) => setInquiry({ openedAt: Date.now(), ...meta });

  return (
    <div className="min-h-screen bg-cream pb-20" data-testid="double-down-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-200/50 via-orange-100/40 to-emerald-100/40 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-12">
          <Link
            to="/programs"
            className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-6"
            data-testid="double-down-back-link"
          >
            <ArrowLeft size={18} /> Back to Programs
          </Link>
          <div className="mb-4">
            <span className="inline-flex items-center gap-2 bg-forest text-cream rounded-full px-4 py-1.5">
              <Zap size={15} />
              <span className="text-xs font-body uppercase tracking-[0.15em]">The Double Down Program</span>
            </span>
          </div>
          <h1 className="font-heading text-4xl md:text-6xl text-forest mb-4 leading-tight">
            The Double Down
          </h1>
          <p className="font-accent italic text-lg md:text-xl text-[#8a6a5a] mb-4">
            A shake up, wake up for your metabolic system.
          </p>
          <p className="text-stone-700 font-body text-lg max-w-2xl leading-relaxed">
            Two Fascial Maneuver practices per day paired with cleansing recipes, meal plans,
            and vitality-boosting elixirs — a dedicated reset to help your body feel like
            something is finally <em>working</em>.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-forest text-cream font-body font-medium px-7 py-3 rounded-full hover:bg-forest-dark transition-colors"
              data-testid="double-down-book-hero"
            >
              Book Now · $109 <ChevronRight size={18} />
            </a>
            <button
              onClick={() => openInquiry({ source: 'double-down-hero' })}
              className="inline-flex items-center gap-2 bg-white/70 border border-forest/30 text-forest font-body font-medium px-6 py-3 rounded-full hover:border-forest transition-colors"
              data-testid="double-down-inquire-hero"
            >
              Inquire to Join <ChevronRight size={18} />
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <span className="inline-flex items-center gap-2 bg-forest/10 text-forest font-body font-medium px-5 py-2.5 rounded-full">
              <Calendar size={15} /> Starts Aug 4
            </span>
            <span className="inline-flex items-center gap-2 bg-terracotta/10 text-terracotta font-body font-medium px-5 py-2.5 rounded-full" data-testid="double-down-earlybird-pill">
              <Sparkles size={15} /> $109
            </span>
            <span className="inline-flex items-center gap-2 bg-white/70 border border-stone-200 text-forest font-body font-medium px-5 py-2.5 rounded-full">
              Sign up until Tues, Aug 4
            </span>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="max-w-3xl mx-auto px-6 mt-14">
        <div className="space-y-5 font-body text-stone-700 leading-relaxed">
          <p className="font-heading text-xl md:text-2xl text-forest italic">
            I don&apos;t really believe in &lsquo;quick fixes&rsquo; — and feeling like something is
            working is extra empowering for our motivation.
          </p>
          <p>
            If you&apos;ve been pounding away at the gym, dieting, biohacking… and not seeing what
            you&apos;re hoping for… or feeling like you have <span className="font-semibold text-forest">NO IDEA</span> what
            to do, where to turn, or how to shift your health — consider the Double Down as a bit of
            a shake up, wake up for your metabolic system.
          </p>
          <p className="font-heading text-lg text-forest">
            It&apos;s good stuff. It&apos;s healthy. It&apos;s not extreme. It IS dedicated.
          </p>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="max-w-5xl mx-auto px-6 mt-16" data-testid="double-down-includes">
        <div className="text-center mb-8">
          <p className="text-xs font-body text-terracotta uppercase tracking-[0.2em] mb-2">What&apos;s Inside</p>
          <h2 className="font-heading text-3xl text-forest">Your Daily Practice</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          {includes.map(({ icon: Icon, title, text }) => (
            <div key={title} className="bg-white rounded-2xl border border-stone-200 p-6 flex items-start gap-4">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-forest/8 text-forest flex-shrink-0">
                <Icon size={20} />
              </div>
              <div>
                <h3 className="font-heading text-lg text-forest mb-1">{title}</h3>
                <p className="font-body text-stone-600 text-sm leading-relaxed">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section className="max-w-5xl mx-auto px-6 mt-16" data-testid="double-down-pricing">
        <div className="text-center mb-8">
          <p className="text-xs font-body text-terracotta uppercase tracking-[0.2em] mb-2">Two Ways to Join</p>
          <h2 className="font-heading text-3xl text-forest">Pricing</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {/* Standalone */}
          <div className="bg-white rounded-2xl border-2 border-forest/30 p-7 flex flex-col">
            <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-1">Stand-Alone Program</p>
            <h3 className="font-heading text-2xl text-forest mb-1">Double Down on its own</h3>
            <div className="flex items-end gap-3 mb-1">
              <p className="font-heading text-4xl text-forest">$109</p>
            </div>
            <p className="font-body text-terracotta text-sm font-medium mb-3">Sign up until Tues, Aug 4</p>
            <p className="font-body text-stone-600 text-sm leading-relaxed mb-6 flex-1">
              The full Double Down experience as a focused, dedicated program — no other commitment required. Starts Aug 4.
            </p>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 bg-forest text-cream font-body font-medium px-6 py-3 rounded-full hover:bg-forest-dark transition-colors mb-2"
              data-testid="double-down-book-standalone"
            >
              Book Now · $109 <ChevronRight size={16} />
            </a>
            <button
              onClick={() => openInquiry({ source: 'double-down-standalone', tierName: 'Double Down — Stand-Alone ($109)' })}
              className="w-full inline-flex items-center justify-center gap-2 bg-white border border-forest/30 text-forest font-body font-medium px-6 py-3 rounded-full hover:border-forest transition-colors"
              data-testid="double-down-inquire-standalone"
            >
              Inquire to Join <ChevronRight size={16} />
            </button>
          </div>

          {/* Add-on */}
          <div className="bg-gradient-to-br from-amber-50 to-emerald-50 rounded-2xl border border-stone-200 p-7 flex flex-col">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={15} className="text-terracotta" />
              <p className="text-xs font-body text-stone-500 uppercase tracking-wider">Flex Detox Add-On</p>
            </div>
            <h3 className="font-heading text-2xl text-forest mb-3">Add it to your Flex Detox tier</h3>
            <ul className="space-y-2.5 mb-6 flex-1">
              {[
                { tier: 'The Glow Up (Tier 1)', price: '+ $49' },
                { tier: 'Deep Detox (Tier 2)', price: '+ $29' },
                { tier: 'Full Immersion (Tier 3)', price: 'Included free' },
              ].map((row) => (
                <li key={row.tier} className="flex items-center justify-between bg-white/70 rounded-xl px-4 py-2.5 border border-stone-200">
                  <span className="font-body text-sm text-stone-700">{row.tier}</span>
                  <span className="font-body text-sm font-semibold text-forest">{row.price}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/programs/flex-detox"
              className="w-full inline-flex items-center justify-center gap-2 bg-white border border-forest/30 text-forest font-body font-medium px-6 py-3 rounded-full hover:border-forest transition-colors"
              data-testid="double-down-view-flex-detox"
            >
              Explore the Flex Detox <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="max-w-3xl mx-auto px-6 mt-16">
        <div className="bg-forest text-cream rounded-3xl p-8 md:p-12 text-center">
          <h3 className="font-heading text-2xl md:text-3xl mb-3">Ready to double down?</h3>
          <p className="font-body text-cream/80 mb-6 max-w-xl mx-auto leading-relaxed">
            Reach out and Jill will help you decide whether to take it on its own or add it to your
            Flex Detox journey. No commitment — just a conversation.
          </p>
          <button
            onClick={() => openInquiry({ source: 'double-down-closing' })}
            className="inline-flex items-center gap-2 bg-cream text-forest font-body font-medium px-8 py-3 rounded-full hover:bg-white transition-colors"
            data-testid="double-down-inquire-closing"
          >
            Inquire to Join <ChevronRight size={18} />
          </button>
          <div className="mt-5">
            <InfoSessionButton
              variant="outline"
              testId="double-down-info-session-link"
              label="Sign Up for a Free Info Session — No Commitment"
            />
          </div>
          <p className="mt-4 text-cream/60 font-body text-sm">
            Prefer email?{' '}
            <Link to="/contact" className="underline hover:text-cream">Contact Jill</Link>
          </p>
        </div>
      </section>

      {/* INQUIRY MODAL */}
      <InquiryFormModal
        key={`double-down-inquiry-${inquiry?.openedAt || 'closed'}`}
        isOpen={!!inquiry}
        onClose={() => setInquiry(null)}
        programName="Double Down Program"
        tierName={inquiry?.tierName}
        source={inquiry?.source}
      />
    </div>
  );
};

const DoubleDownPage = () => {
  const { hasAccess } = useAuth();
  return hasAccess('double-down') ? <DoubleDownProgram /> : <DoubleDownSales />;
};

export default DoubleDownPage;
